
import React from "react";
import { Edit, Trash2, FileText, Calendar, Eye } from "react-feather";
import { CaseStudyData } from "../interface/case-study";


interface CaseStudyCardProps {
  caseStudy: CaseStudyData;
  onEdit: (caseStudy: CaseStudyData) => void;
  onDelete: (caseStudyId: string) => void;
  onView?: (caseStudy: CaseStudyData) => void;
}

const CaseStudyCard: React.FC<CaseStudyCardProps> = ({
  caseStudy,
  onEdit,
  onDelete,
  onView,
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };


  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    e.currentTarget.src = "/images/placeholder-image.png"; // Fallback image
  };

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden transition-shadow duration-200 hover:shadow-lg">
      {/* Image Section */}
      <div className="relative h-48 bg-gray-100">
        <img
          src={caseStudy.image_url}
          alt={caseStudy.title}
          className="w-full h-full object-cover"
          onError={handleImageError}
        />
        {/* Action Buttons */}
        <div className="absolute top-3 right-3 flex space-x-2">
          {onView && (
            <button
              onClick={() => onView(caseStudy)}
              className="p-2 bg-white bg-opacity-90 rounded-full shadow-md hover:bg-opacity-100 transition-all duration-200"
              title="View Case Study"
            >
              <Eye size={16} className="text-blue-600" />
            </button>
          )}
          <button
            onClick={() => onEdit(caseStudy)}
            className="p-2 bg-white bg-opacity-90 rounded-full shadow-md hover:bg-opacity-100 transition-all duration-200"
            title="Edit Case Study"
          >
            <Edit size={16} className="text-green-600" />
          </button>
          <button
            onClick={() => onDelete(caseStudy.id)}
            className="p-2 bg-white bg-opacity-90 rounded-full shadow-md hover:bg-opacity-100 transition-all duration-200"
            title="Delete Case Study"
          >
            <Trash2 size={16} className="text-red-600" />
          </button>
        </div>
      </div>

      {/* Content Section */}
      <div className="p-4">
        {/* Title */}
        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
          {caseStudy.title}
        </h3>

        {/* Description */}
        <p className="text-gray-600 text-sm mb-3 line-clamp-3">
          {caseStudy.description}
        </p>

        {/* Meta Information */}
        <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
          <div className="flex items-center">
            <Calendar size={12} className="mr-1" />
            <span>Published: {formatDate(caseStudy.published_date.toString())}</span>
          </div>
          <div className="flex items-center">
            <span>ID: {caseStudy.id.toString()}</span>
          </div>
        </div>

        {/* File Download Link */}
        {caseStudy.file_url && (
          <div className="pt-3 border-t border-gray-100">
            <a
              href={caseStudy.file_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors duration-200"
            >
              <FileText size={14} className="mr-1" />
              Download PDF
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

    export default CaseStudyCard;